<template>
  <div class="ocr-processor">
    <h2>📝 OCR Processor</h2>

    <!-- Input -->
    <div class="input-section">
      <div class="file-input-container">
        <input
          ref="fileInput"
          type="file"
          @change="handleFileSelect"
          accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp"
          class="file-input"
          id="file-upload"
        />
        <label for="file-upload" class="file-input-label">
          📁 Choose File (PDF or Image)
        </label>
        <span v-if="selectedFile" class="file-name">
          {{ selectedFile.name }}
        </span>
      </div>

      <button
        @click="runOcr"
        :disabled="loading || !selectedFile"
        class="primary-btn"
      >
        {{ loading ? "⏳ Processing..." : "Run OCR" }}
      </button>
    </div>

    <!-- Progress indicator -->
    <div v-if="loading" class="progress-container">
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: progress + '%' }"></div>
      </div>
      <p class="progress-text">{{ progressText }}</p>
    </div>

    <!-- Status -->
    <transition name="fade">
      <div v-if="ocrStatus" class="status-card" :class="{ error: isError }">
        <p>{{ ocrStatus }}</p>

        <div v-if="finalMeta">
          <p>
            📂 File: <strong>{{ finalMeta.filename }}</strong>
          </p>
          <p>
            📁 Output Dir: <code>{{ finalMeta.output_dir }}</code>
          </p>
          <p>
            📊 Status: <strong>{{ finalMeta.status }}</strong>
          </p>
          <p>
            📑 Pages: <strong>{{ finalMeta.summary?.total_pages }}</strong> | 📋
            Rows: <strong>{{ finalMeta.summary?.total_rows }}</strong>
          </p>
        </div>
      </div>
    </transition>

    <!-- Side-by-side viewer -->
    <div class="viewer">
      <!-- Left: raw file preview -->
      <div class="viewer-left">
        <h3>📂 File Preview</h3>
        <div v-if="filePreview" class="file-preview">
          <div v-if="isPdf" class="pdf-info" v-html="filePreview"></div>
          <img v-else-if="isImage" :src="filePreview" alt="File preview" />
          <p v-else>No preview available</p>
        </div>
        <div v-else class="placeholder">👆 Select a file to see preview</div>
      </div>

      <!-- Right: OCR results -->
      <div class="viewer-right">
        <h3>🔎 OCR Results</h3>

        <!-- Debug info -->
        <div v-if="ocrResults.length === 0 && finalMeta" class="debug-info">
          <p><strong>Debug Info:</strong></p>
          <pre>{{ JSON.stringify(finalMeta, null, 2) }}</pre>
        </div>

        <div v-if="ocrResults.length > 0" class="ocr-results">
          <div
            v-for="page in ocrResults"
            :key="'page-' + page.page"
            class="page-block"
          >
            <details open>
              <summary>
                📄 Page {{ page.page }} (Rows:
                {{ page.total_rows || page.table_rows?.length || 0 }})
              </summary>
              <p v-if="page.column_separator">
                📐 Column Separator: {{ page.column_separator }}
              </p>

              <div v-if="page.table_rows && page.table_rows.length > 0">
                <table>
                  <thead>
                    <tr>
                      <th>Content</th>
                      <th>Translation/Right</th>
                      <th>Y Position</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, idx) in page.table_rows"
                      :key="'row-' + idx"
                    >
                      <td>{{ row.left || row.content || "" }}</td>
                      <td>{{ row.right || row.translation || "" }}</td>
                      <td>{{ row.y_pos !== undefined ? row.y_pos : "" }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="no-data">
                No table rows found for this page
              </div>
            </details>
          </div>
        </div>
        <p v-else-if="!finalMeta" class="placeholder">
          👈 Upload a file and run OCR to see results
        </p>
        <div
          v-else-if="finalMeta && ocrResults.length === 0"
          class="no-results"
        >
          <p>OCR completed but no results to display</p>
          <button @click="debugResults" class="debug-btn">Show Raw Data</button>
        </div>
      </div>
    </div>

    <!-- Raw JSON Debug Modal (if needed) -->
    <div v-if="showDebug" class="debug-modal" @click="showDebug = false">
      <div class="debug-content" @click.stop>
        <h3>Raw OCR Data</h3>
        <pre>{{ JSON.stringify(finalMeta, null, 2) }}</pre>
        <button @click="showDebug = false" class="close-btn">Close</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted } from "vue";

const fileInput = ref(null);
const selectedFile = ref(null);
const ocrStatus = ref(null);
const ocrResults = ref([]);
const finalMeta = ref(null);
const loading = ref(false);
const isError = ref(false);
const filePreview = ref(null);
const progress = ref(0);
const progressText = ref("");
const showDebug = ref(false);

const isPdf = computed(() => selectedFile.value?.type === "application/pdf");
const isImage = computed(() => selectedFile.value?.type.startsWith("image/"));

// Simple PDF info display
const displayPdfInfo = async (file) => {
  const fileSizeKB = Math.round(file.size / 1024);
  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
  return `
    <div style="padding: 20px; text-align: center; background: #f3f4f6; border-radius: 8px;">
      <h4 style="margin: 0 0 10px 0; color: #374151;">📄 PDF Document</h4>
      <p style="margin: 5px 0; color: #6b7280;">File: ${file.name}</p>
      <p style="margin: 5px 0; color: #6b7280;">Size: ${
        fileSizeKB > 1024 ? fileSizeMB + " MB" : fileSizeKB + " KB"
      }</p>
      <p style="margin: 10px 0 0 0; color: #059669; font-weight: 500;">✅ Ready for OCR processing</p>
    </div>
  `;
};

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

async function handleFileSelect(event) {
  const file = event.target.files[0];
  if (!file) return;

  selectedFile.value = file;
  filePreview.value =
    file.type === "application/pdf"
      ? await displayPdfInfo(file)
      : file.type.startsWith("image/")
      ? await blobToDataURL(file)
      : null;
}

// Debug function to show raw results
function debugResults() {
  showDebug.value = true;
}

// Process final result and populate ocrResults
function processFinalResult(data) {
  console.log("Processing final result:", data);

  finalMeta.value = data.final_result;

  // Check if we have the value array
  if (data.final_result?.value && Array.isArray(data.final_result.value)) {
    ocrResults.value = data.final_result.value.map((page, index) => ({
      page: page.page || index + 1,
      column_separator: page.column_separator || null,
      total_rows: page.total_rows || page.table_rows?.length || 0,
      table_rows: page.table_rows || [],
    }));

    console.log("OCR Results processed:", ocrResults.value);
  } else {
    console.warn("No value array found in final result");
    ocrResults.value = [];
  }

  ocrStatus.value = "✅ OCR completed successfully";
  progress.value = 100;
  progressText.value = "Complete!";
}

async function runOcr() {
  if (!selectedFile.value) {
    ocrStatus.value = "❌ Please select a file first";
    isError.value = true;
    return;
  }

  loading.value = true;
  isError.value = false;
  ocrStatus.value = "Starting OCR...";
  ocrResults.value = [];
  finalMeta.value = null;
  progress.value = 0;
  progressText.value = "Preparing...";

  try {
    const apiBase = process.env.VUE_APP_API_BASE_URL || "http://localhost:8000";
    const wsBase = apiBase.replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/ocr_ws`);
    ws.binaryType = "arraybuffer";

    ws.onopen = async () => {
      console.log("WebSocket connected");
      ws.send(JSON.stringify({ filename: selectedFile.value.name }));

      const chunkSize = 4096;
      let offset = 0;
      while (offset < selectedFile.value.size) {
        const slice = selectedFile.value.slice(offset, offset + chunkSize);
        const buffer = await slice.arrayBuffer();
        ws.send(buffer);
        offset += chunkSize;

        progress.value = Math.round((offset / selectedFile.value.size) * 60);
        progressText.value = `Uploading... ${progress.value}%`;
      }
      ws.send("__EOF__");
      progressText.value = "File uploaded, processing OCR...";
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("WebSocket message:", data);

        if (data.page_done) {
          const page = {
            page:
              data.page_done.page_number ??
              data.page_done.page ??
              ocrResults.value.length + 1,
            table_rows: (data.page_done.table_rows || []).map((r) => ({
              left: r.left ?? "",
              right: r.right ?? "",
              y_pos: r.y_pos ?? null,
            })),
            total_rows: data.page_done.table_rows?.length || 0,
          };
          ocrResults.value.push(page);
          progress.value = Math.min(95, progress.value + 5);
          progressText.value = `Processed page ${page.page}`;
        } else if (data.final_result) {
          processFinalResult(data);
          ws.close();
        } else if (data.error) {
          ocrStatus.value = `❌ Error: ${data.error}`;
          isError.value = true;
          ws.close();
        }
      } catch (err) {
        console.error("📩 Non-JSON WS message:", event.data, err);
      }
    };

    ws.onerror = (err) => {
      console.error("WebSocket error:", err);
      ocrStatus.value = "❌ WebSocket connection failed";
      isError.value = true;
      loading.value = false;
    };

    ws.onclose = (event) => {
      console.log("WebSocket closed:", event.code, event.reason);
      loading.value = false;
    };
  } catch (error) {
    console.error("OCR Error:", error);
    ocrStatus.value = `❌ OCR failed: ${error.message}`;
    isError.value = true;
    loading.value = false;
  }
}

onUnmounted(() => {
  if (filePreview.value && filePreview.value.startsWith("blob:")) {
    URL.revokeObjectURL(filePreview.value);
  }
});
</script>

<style scoped>
.ocr-processor {
  max-width: 1200px;
  margin: auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.input-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.file-input-container {
  display: flex;
  align-items: center;
  gap: 15px;
}

.file-input {
  display: none;
}

.file-input-label {
  display: inline-block;
  padding: 12px 20px;
  background: #3b82f6;
  color: white;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-weight: 500;
}

.file-input-label:hover {
  background: #2563eb;
}

.file-name {
  color: #059669;
  font-weight: 500;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.primary-btn {
  background: #059669;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s;
  align-self: flex-start;
}

.primary-btn:hover:not(:disabled) {
  background: #047857;
  transform: translateY(-1px);
}

.primary-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.progress-container {
  margin-bottom: 20px;
  padding: 16px;
  background: #fef3c7;
  border-radius: 8px;
  border: 1px solid #fbbf24;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #fed7aa;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #f59e0b, #d97706);
  transition: width 0.3s ease;
  border-radius: 4px;
}

.progress-text {
  margin: 0;
  font-size: 14px;
  color: #92400e;
  font-weight: 500;
}

.status-card {
  background: #ecfdf5;
  border: 1px solid #34d399;
  padding: 16px;
  margin-bottom: 20px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.status-card.error {
  background: #fef2f2;
  border-color: #f87171;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.viewer {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

@media (max-width: 768px) {
  .viewer {
    grid-template-columns: 1fr;
  }
}

.viewer-left,
.viewer-right {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.viewer-left h3,
.viewer-right h3 {
  margin: 0 0 16px 0;
  color: #374151;
  font-size: 18px;
  font-weight: 600;
}

.file-preview {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  background: #f9fafb;
}

.pdf-info {
  padding: 0;
}

.pdf-info > div {
  margin: 0;
}

.file-preview img {
  width: 100%;
  height: 400px;
  object-fit: contain;
  display: block;
}

.placeholder {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 8px;
  border: 2px dashed #d1d5db;
}

.debug-info {
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.debug-info pre {
  font-size: 12px;
  max-height: 200px;
  overflow: auto;
  background: #fff;
  padding: 8px;
  border-radius: 4px;
}

.no-results {
  text-align: center;
  padding: 20px;
  color: #6b7280;
}

.debug-btn {
  background: #f59e0b;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 10px;
}

.debug-btn:hover {
  background: #d97706;
}

.ocr-results {
  max-height: 500px;
  overflow-y: auto;
}

.page-block {
  margin-bottom: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.page-block summary {
  background: #f8fafc;
  padding: 12px 16px;
  cursor: pointer;
  font-weight: 500;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.page-block summary:hover {
  background: #f1f5f9;
}

.no-data {
  padding: 20px;
  text-align: center;
  color: #6b7280;
  font-style: italic;
}

.ocr-results table {
  width: 100%;
  border-collapse: collapse;
}

.ocr-results th,
.ocr-results td {
  border: 1px solid #e5e7eb;
  padding: 12px;
  text-align: left;
  vertical-align: top;
}

.ocr-results th {
  background: #f8fafc;
  font-weight: 600;
  color: #374151;
}

.ocr-results td {
  font-size: 14px;
  line-height: 1.5;
}

.ocr-results tbody tr:hover {
  background: #f9fafb;
}

.debug-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.debug-content {
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 80vw;
  max-height: 80vh;
  overflow: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.debug-content h3 {
  margin: 0 0 16px 0;
}

.debug-content pre {
  font-size: 12px;
  background: #f8fafc;
  padding: 16px;
  border-radius: 8px;
  overflow: auto;
  max-height: 60vh;
}

.close-btn {
  background: #6b7280;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 16px;
}

.close-btn:hover {
  background: #4b5563;
}
</style>
